import React, { useEffect, useRef, useState } from "react";
import { Moon, Sparkles } from "lucide-react";
import { BedtimeNudge } from "./BedtimeNudge";
import { PlanTour, TOUR_KEY } from "./PlanTour";
import { Sheet, SheetContent } from "../ui/sheet";
import { NightCreator } from "./NightCreator";
import { PracticeSheet } from "./PracticeSheet";
import { PracticePlayer } from "./PracticePlayer";
import { SERIF, getNight, getPlan } from "./practiceApi";

// After an installation exists: 21-Day Plan (primary) + Night Version — never forced.
export const PracticeOffer = ({ journeyId, onStarted, heading = true }) => {
  const [plan, setPlan] = useState(false);
  const [nightOpen, setNightOpen] = useState(false);
  const [night, setNight] = useState(null);
  const [active, setActive] = useState(null);
  const refresh = () => {
    getNight(journeyId).then(setNight).catch(() => {});
    getPlan().then((p) => setActive(p?.journey_id === journeyId ? p : null)).catch(() => {});
  };
  useEffect(refresh, [journeyId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener("soulumo:practice-ended", refresh);
    return () => window.removeEventListener("soulumo:practice-ended", refresh);
  }, [journeyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const askedPlan = useRef(new URLSearchParams(window.location.search).get("plan") === "1");
  useEffect(() => {
    // Arrived from "Add to my library" on a gift: open the 21-Day set-up once, unless a plan is already running.
    if (!askedPlan.current) return;
    getPlan().then((p) => { if (!p && askedPlan.current) { askedPlan.current = false; openPlan(); } }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const nightRef = useRef(null);
  const [tour, setTour] = useState(false);
  const openPlan = () => {
    if (localStorage.getItem(TOUR_KEY)) return setPlan(true);
    localStorage.setItem(TOUR_KEY, "1");
    setTour(true);
  };
  const ready = night?.status === "ready";
  useEffect(() => {
    if (ready && new URLSearchParams(window.location.search).get("night") === "1") {
      setTimeout(() => nightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 600);
    }
  }, [ready]);
  const building = night && ["queued", "building"].includes(night.status);
  return (
    <div className="space-y-3" data-testid="practice-offer">
      {heading && <p className="text-base italic text-[#cfc6ae]" style={{ fontFamily: SERIF }}>Now let's help these words become familiar, during your day and while you rest.</p>}
      {active ? (
        <p className="text-sm text-[#cfc6ae]" data-testid="practice-offer-active">21-Day Practice · Day {active.day} of {active.days} · shown at the top of your library</p>
      ) : (
        <button type="button" onClick={openPlan} data-testid="practice-offer-plan-btn"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#d7bb7a] py-3.5 text-xs uppercase tracking-[0.26em] text-[#0b1a33] transition-opacity hover:opacity-90">
          <Sparkles size={14} /> Create My 21-Day Plan
        </button>
      )}
      {night?.status === "ready" ? (
        <div className="rounded-2xl border border-[#d7bb7a]/30 p-3" data-testid="practice-offer-night-ready" ref={nightRef}>
          <PracticePlayer kind="night" src={night.audio_url} title={`Night Installation · ${night.label}`} />
          <BedtimeNudge journeyId={journeyId} />
          <button type="button" onClick={() => setNightOpen(true)} data-testid="practice-offer-night-remake-btn"
            className="mt-2 text-xs text-[#cfc6ae] underline underline-offset-4 transition-colors hover:text-[#f3ecd9]">
            Make a new Night Version
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setNightOpen(true)} data-testid="practice-offer-night-btn"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#d7bb7a]/60 py-3 text-xs uppercase tracking-[0.24em] text-[#f3ecd9] transition-colors hover:bg-[#d7bb7a]/15">
          <Moon size={14} /> {building ? "Night Version · creating…" : "Create Night Version"}
        </button>
      )}
      <PlanTour open={tour} onDone={(go) => { setTour(false); if (go) setPlan(true); }} />
      <PracticeSheet open={plan} onOpenChange={(o) => { setPlan(o); if (!o) refresh(); }} journeyId={journeyId} onStarted={onStarted} />
      <Sheet open={nightOpen} onOpenChange={(o) => { setNightOpen(o); if (!o) refresh(); }}>
        <SheetContent side="bottom" className="border-[#2b3d5e] bg-[#0b1a33] px-6 pb-10 pt-8 text-left text-[#f3ecd9]" data-testid="night-sheet">
          <NightCreator journeyId={journeyId} fresh={night?.status === "ready"} onReady={setNight} />
        </SheetContent>
      </Sheet>
    </div>
  );
};
